import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProjectEstimateStatusDescription } from '@app/shared/data/project-enums';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ChartModule } from '@progress/kendo-angular-charts';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { ExcelModule, GridModule } from '@progress/kendo-angular-grid';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { LoggerService } from 'app/common/services/logger.service';
import { Subject } from 'rxjs';
import { ProjectEstimatePhaseStepDetail } from '../../models/project-estimate-phase-step-detail';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';

interface ProjectEstimate {
  ProjectEstimateId: number;
  EstimateVersion: number;
  ProjectEstimateStatusId: number;
  RecordStatus: string;
  RecordType: any;
  DisplayText: string;
}

enum ProjectEstimateStatus {
  Draft = 1,
  LatestPublished,
  PreviouslyPublished
}

@Component({
  selector: 'app-project-drilling-phase-step',
  templateUrl: './project-drilling-phase-step.component.html',
  styleUrls: ['./project-drilling-phase-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ChartModule,
    DropDownListComponent,
    ExcelModule,
    FormsModule,
    GridModule,
    LoaderComponent,
  ],
})
export class ProjectDrillingPhaseStepComponent implements OnInit {
  private lookupPlusService = inject(LookupPlusService);
  private loggerService = inject(LoggerService);
  private cdr = inject(ChangeDetectorRef);

  private tProjectDrillingPhaseStepDetail: string = 'ProjectEstimatePhaseStepDetails';
  private tProjectEstimate: string = 'ProjectEstimates';
  private selectedEstimateId: number;
  private priorPhase: string;
  private priorCumulativeDays: number;
  private priorCumulativeCost: number;
  private priorDaysInPhase: number;

  public icons = kendoIcons;
  public dProjectEstimatePhaseStepDetail = new Array<ProjectEstimatePhaseStepDetail>();
  public dProjectEstimate = new Array<ProjectEstimate>();
  public errorMessage: string;
  public costMinValue: number;
  public costMaxValue: number;
  public daysMinValue: number;
  public daysMaxValue: Number;
  public depthMinValue: number;
  public depthMaxValue: number;
  public exportFileName: string;
  public projectEstimateId: number;
  public loading: boolean;

  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly refresh = input<Subject<boolean>>(undefined);

  private async runTasks(): Promise<void> {
    let estimateString = `$expand=RecordType&$filter=ProjectId%20eq%20${this.iProjectId()}&$select=ProjectEstimateId%2C%20RecordStatus%2C%20EstimateVersion%2C%20ProjectEstimateStatusId&$orderby=EstimateVersion%20desc`;

    const payload = await this.lookupPlusService.getLookup(this.tProjectEstimate, estimateString);
    Object.assign(this.dProjectEstimate, payload.value);
    this.dProjectEstimate.forEach((v, idx) => {
      this.dProjectEstimate[idx].DisplayText = `${v.ProjectEstimateId}/ V-${v.EstimateVersion}/ ${v.RecordStatus === null ? '' : v.RecordStatus + '/ '} ${v.RecordType === null ? '' : v.RecordType.Description + '/ '} ${this.getEstimateStatus(v.ProjectEstimateStatusId)}`;
    });
    if (this.dProjectEstimate.length > 0) {
      let testPublishedId = this.dProjectEstimate.findIndex(x => x.ProjectEstimateStatusId === ProjectEstimateStatus.LatestPublished);
      if (testPublishedId !== -1) {
        this.selectedEstimateId = this.dProjectEstimate[testPublishedId].ProjectEstimateId;
      } else {
        let testDraftId = this.dProjectEstimate.findIndex(x => x.ProjectEstimateStatusId === ProjectEstimateStatus.Draft);
        if (testDraftId !== -1) {
          this.selectedEstimateId = this.dProjectEstimate[testDraftId].ProjectEstimateId;
        } else {
          this.selectedEstimateId = null;
        }
      }
    }
    if (this.selectedEstimateId !== null && this.selectedEstimateId !== undefined) {
      this.projectEstimateId = this.selectedEstimateId;
      let phaseStepString = `$filter=ProjectEstimateId eq ${this.selectedEstimateId} and (Duration gt 0 or Cost gt 0)`;
      const phaseStepPayload = await this.lookupPlusService.getLookup(this.tProjectDrillingPhaseStepDetail, phaseStepString);
      Object.assign(this.dProjectEstimatePhaseStepDetail, phaseStepPayload.value);
      this.generateChartData();
    } else {
      let err: string = 'Warning : No Estimates, cannot generate phase/step data';
      this.loggerService.warn(err);
    }
  }

  public handleEstimateChange(estimateId: number) {
    this.loading = true;
    this.selectedEstimateId = estimateId;
    this.projectEstimateId = this.selectedEstimateId;
    let phaseStepString = `$filter=ProjectEstimateId eq ${this.selectedEstimateId}  and (Duration gt 0 or Cost gt 0)`;
    this.lookupPlusService.getLookup(this.tProjectDrillingPhaseStepDetail, phaseStepString)
      .then(payload => {
        this.initVariables();
        this.dProjectEstimatePhaseStepDetail = [];
        Object.assign(this.dProjectEstimatePhaseStepDetail, payload.value);
        this.generateChartData();
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  private generateChartData(): void {
    if (this.dProjectEstimatePhaseStepDetail.length > 0) {
      this.dProjectEstimatePhaseStepDetail.forEach(element => {
        if (element.Phase !== this.priorPhase) {
          this.priorDaysInPhase = 0;
        }
        element.DaysInPhase = element.Duration / 24.0;
        element.CumulativeDays = this.priorCumulativeDays + element.Duration / 24.0;
        element.CumulativeCost = this.priorCumulativeCost + element.Cost;
        this.priorDaysInPhase = element.DaysInPhase;
        this.priorCumulativeDays = element.CumulativeDays;
        this.priorCumulativeCost = element.CumulativeCost;
        this.priorPhase = element.Phase;
      });

      this.costMinValue = 0;
      this.costMaxValue = this.getMaxCost();
      this.daysMinValue = this.getMinDays();
      this.daysMaxValue = this.getMaxDays();
      this.depthMinValue = this.getMinDepth();
      this.depthMaxValue = this.getMaxDepth();
    }
  }

  private getMaxCost(): number {
    let maxIndex: number = this.dProjectEstimatePhaseStepDetail.length - 1;
    const maxReducer = (accumulator: number, currentValue: ProjectEstimatePhaseStepDetail) =>
      currentValue.CumulativeCost > accumulator ? currentValue.CumulativeCost : accumulator;
    return this.dProjectEstimatePhaseStepDetail.reduce(maxReducer, this.dProjectEstimatePhaseStepDetail[maxIndex].CumulativeCost);
  }

  private getMinDays(): number {
    const minReducer = (accumulator: number, currentValue: ProjectEstimatePhaseStepDetail) =>
      currentValue.CumulativeDays < accumulator ? currentValue.CumulativeDays : accumulator;
    return this.dProjectEstimatePhaseStepDetail.reduce(minReducer, this.dProjectEstimatePhaseStepDetail[0].CumulativeDays);
  }

  private getMaxDays(): number {
    let maxIndex: number = this.dProjectEstimatePhaseStepDetail.length - 1;
    const maxReducer = (accumulator: number, currentValue: ProjectEstimatePhaseStepDetail) =>
      currentValue.CumulativeDays > accumulator ? currentValue.CumulativeCost : accumulator;
    return this.dProjectEstimatePhaseStepDetail.reduce(maxReducer, this.dProjectEstimatePhaseStepDetail[maxIndex].CumulativeDays);
  }

  private getMinDepth(): number {
    const minReducer = (accumulator: number, currentValue: ProjectEstimatePhaseStepDetail) =>
      currentValue.DepthStart < accumulator ? currentValue.DepthStart : accumulator;
    return this.dProjectEstimatePhaseStepDetail.reduce(minReducer, this.dProjectEstimatePhaseStepDetail[0].DepthStart);
  }

  private getMaxDepth(): number {
    let maxIndex: number = this.dProjectEstimatePhaseStepDetail.length - 1;
    const maxReducer = (accumulator: number, currentValue: ProjectEstimatePhaseStepDetail) =>
      currentValue.DepthEnd > accumulator ? currentValue.DepthEnd : accumulator;
    return this.dProjectEstimatePhaseStepDetail.reduce(maxReducer, this.dProjectEstimatePhaseStepDetail[maxIndex].DepthEnd);
  }

  private getEstimateStatus(statusId): string {
    switch (statusId) {
      case 1:
        return ProjectEstimateStatusDescription.Draft;
      case 2:
        return ProjectEstimateStatusDescription.LatestPublished;
      case 3:
        return ProjectEstimateStatusDescription.PreviouslyPublished;
      default:
        return '';
    }
  }

  ngOnInit() {
    this.exportFileName = `projectid-${this.iProjectId()}-drill-phase-step.xlsx`;
    this.priorPhase = 'zero';
    this.initVariables();
    this.runTasks();

    if (this.refresh() != null) {
      this.refresh().subscribe(v => {
        if (!v) return;

        this.handleEstimateChange(this.selectedEstimateId);
      })
    }
  }

  initVariables() {
    this.priorDaysInPhase = 0;
    this.priorCumulativeDays = 0;
    this.priorCumulativeCost = 0;
  }
}